// The one switch that turns the simulated verification UI on, and the reason
// it is a runtime check of the URL rather than a build-time flag.
//
// WHAT THIS IS FOR. Recording a demo of the verification UI before the Solana
// program is deployed anywhere public. In fixture mode NOTHING IS VERIFIED:
// the glyph, the chart and their timings are scripted (or, with
// `source=real`, hash real bytes against fabricated expectations), and no
// claim the UI makes is backed by a chain read.
//
// WHY THE QUERY STRING, NOT A BUILD FLAG. The previous approach (a build-time
// env var, see the deleted demoMode.ts) baked simulation into the bundle at
// build time — which meant a build could only ever be "all real" or "all
// fake", with no way to demonstrate both a passing take and a failing take
// from the same deploy, and a real risk of the flag leaking into a production
// build (it did — see the plan's "Why now" section). Reading the query
// string instead makes activation per-URL: ordinary traffic to a video page
// never carries `?verify=fixture` and is therefore never simulated, while a
// specific link can be shared for a demo or a recording.
//
// THIS IS DELIBERATELY NOT HARDENED AGAINST PRODUCTION USE. See the plan's
// Out of Scope: `fixtureRequest` is the single choke point where that gating
// will later be added.
import { network } from '../../solana/network.js'

export interface FixtureRequest {
  source: 'synthetic' | 'real'
  failSlice: number | null
}

/** Parses `failSlice`: non-negative integers only. Anything else — negative,
 * fractional, non-numeric, empty — yields null rather than a guess. */
const parseFailSlice = (raw: string | null): number | null => {
  if (raw === null || raw === '') return null
  if (!/^\d+$/.test(raw)) return null
  return Number(raw)
}

/**
 * Parses the fixture query grammar:
 *
 *   ?verify=fixture                 fixture on, synthetic source, never fails
 *   ?verify=fixture&failSlice=72    slices 0-71 pass; slice 72 mismatches
 *   ?verify=fixture&source=real     hash real streamed bytes, fake expectations
 *
 * Returns null when `?verify=fixture` is absent — the real path, unchanged.
 */
/**
 * TEMPORARY — DEMO MODE. Whether a page load with no `?verify=` simulates is
 * solana/network.ts's call: the browser's remembered verify setting, else
 * fixture while DEMO_BY_DEFAULT there is true. Every public page in demo shows
 * verification passing whether or not anything was checked — the console
 * banner below says so on every load.
 *
 * `?verify=real` opts out and `?verify=fixture` opts in; network.ts also
 * remembers either for later visits.
 */
export function fixtureRequest(search: string, demoByDefault: boolean = network.demo): FixtureRequest | null {
  const params = new URLSearchParams(search)
  const verify = params.get('verify')
  // Explicit opt-out always wins, so the real path stays reachable in a
  // browser without a rebuild.
  if (verify === 'real') return null
  if (verify !== 'fixture' && !demoByDefault) return null
  const source = params.get('source') === 'real' ? 'real' : 'synthetic'
  return { source, failSlice: parseFailSlice(params.get('failSlice')) }
}

/**
 * The fixture request as it was when the page was FIRST loaded.
 *
 * This is captured at boot rather than read live, because it cannot be read
 * live. `/video/<id>` redirects itself to `/video/<slug>` (routes.tsx, the
 * `video` route takes a `key` that may be an id, a slug or a container id),
 * and that redirect re-navigates without carrying the incoming query string —
 * so by the time `VerifiedPlayer` renders, `window.location.search` is empty
 * and `?verify=fixture` has silently vanished. `installDebugConsole` already
 * solves the same problem the same way for `?debug=true` (see index.tsx).
 *
 * `undefined` means "not captured yet"; `null` means "captured, and this page
 * load did not ask for fixture mode".
 */
let captured: FixtureRequest | null | undefined

/** Called once from index.tsx before anything renders. Idempotent: a second
 * call cannot change what the first one saw, so nothing later in the page's
 * life can turn fixture mode on. */
export function captureFixtureRequest(search: string): void {
  if (captured === undefined) captured = fixtureRequest(search)
}

/** What the whole app should ask. Falls back to reading the live location if
 * capture never happened (a test, or an entry point that forgot to call it) —
 * that read is correct whenever the URL has not been rewritten yet. */
export function activeFixtureRequest(): FixtureRequest | null {
  if (captured === undefined) {
    captured = typeof window === 'undefined' ? null : fixtureRequest(window.location.search)
  }
  return captured
}

/** Test-only: forget the capture so each case starts from a clean page load. */
export function resetCapturedFixtureRequest(): void {
  captured = undefined
}

let warned = false

/**
 * Says plainly, once per page load, that the verification UI is simulated.
 * Once rather than per render: a player that re-renders on every timeupdate
 * would otherwise bury the rest of the console.
 */
export function warnOnce(): void {
  if (warned) return
  warned = true
  // eslint-disable-next-line no-console
  console.warn(
    '[forum] SIMULATED VERIFICATION IS ON (?verify=fixture). ' +
      'The verification glyph and modal are scripted for a product demo. ' +
      'No hashes are computed and no chain is read — nothing on screen is verified.',
  )
}
